import { hashString } from "../shared/hash";
import { hasAction, type AbilityDef, type MatchRules, type SuperDef } from "../sim";
import { ABILITIES } from "./abilities";
import { ARENAS } from "./arenas/foundry";
import { HEROES } from "./heroes";
import { SUPERS } from "./supers/squall";

export const ROUND_SECONDS = 90;
export const ROUNDS_TO_WIN = 2;

/** Ярость: полная шкала — примерно за полраунда активного боя (замер ботами — docs/design/design-doc.md). */
export const METER_RULES = {
  // бот теперь блокирует серии (05.11.2026): ударов в блок больше — прирост за блок меньше, шкала ~40 % раунда;
  // бот берёт все приёмы (05.11.2026, издёвки тоже копят Ярость) — прирост за удар на 8 % меньше
  hitAttacker: 5,
  hitDefender: 3.3,
  blockAttacker: 1,
  blockDefender: 1.5,
  carryOver: true,
} as const;

export class ContentError extends Error {
  override name = "ContentError";
}

function kitOf(heroId: string): AbilityDef[] {
  const hero = HEROES.get(heroId);
  if (!hero) throw new ContentError(`Нет героя «${heroId}»`);
  return hero.kit.map((id) => {
    const ability = ABILITIES.get(id);
    if (!ability) throw new ContentError(`У героя «${heroId}» нет способности «${id}»`);
    for (const cue of ability.actions ?? []) {
      if (!hasAction(cue.type)) throw new ContentError(`Способность «${id}»: нет действия «${cue.type}»`);
    }
    for (const target of [
      ability.next,
      ...Object.values(ability.branches ?? {}),
      ...Object.values(ability.backBranches ?? {}),
      ...Object.values(ability.downBranches ?? {}),
    ]) {
      if (target !== undefined && !hero.kit.includes(target))
        throw new ContentError(`Способность «${id}»: ветка комбо ведёт в «${target}», а его нет у героя «${heroId}»`);
    }
    return ability;
  });
}

function superOf(id: string | undefined): SuperDef | null {
  if (!id) return null;
  const move = SUPERS.get(id);
  if (!move) throw new ContentError(`Нет суперприёма «${id}»`);
  return move;
}

/** Правила матча из содержимого: герои, их наборы, арена. Ошибка содержимого — сразу, до боя. */
export function buildRules(heroIds: readonly [string, string], arenaId: string): MatchRules {
  const arena = ARENAS.get(arenaId);
  const [first, second] = heroIds.map((id) => HEROES.get(id));
  if (!arena) throw new ContentError(`Нет арены «${arenaId}»`);
  if (!first || !second) throw new ContentError(`Нет героя «${heroIds.join(", ")}»`);
  return {
    heroes: [first, second],
    kits: [kitOf(heroIds[0]), kitOf(heroIds[1])],
    supers: [superOf(first.super), superOf(second.super)],
    arena,
    meter: METER_RULES,
    // Джон, 04.11.2026 17:51: «Я не хочу такие стоп кадры». Сила удара — в отбросе, искрах, тряске и вибрации
    hitstop: false,
    roundSeconds: ROUND_SECONDS,
    roundsToWin: ROUNDS_TO_WIN,
  };
}

/** Строка с ключами по алфавиту: одинаковое содержимое — одинаковая строка в любом браузере. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

/** Хеш версии содержимого: всё, что влияет на бой. Онлайн стороны сверяют его до боя (docs/design/abilities.md). */
export function contentHash(): string {
  const all = {
    abilities: [...ABILITIES.values()],
    supers: [...SUPERS.values()],
    heroes: [...HEROES.values()],
    arenas: [...ARENAS.values()],
    rules: { ROUND_SECONDS, ROUNDS_TO_WIN, METER_RULES, hitstop: false },
  };
  return hashString(canonical(all)).toString(16).padStart(8, "0");
}
